import { ProblemError } from "../api/problem";
import { ErrorState } from "./feedback";

/**
 * THE SINGLE MOST COMMON DEFECT IN THIS REPO, MADE HARD TO REPEAT.
 *
 * A functional sweep of all four SPAs found the same bug ~25 times, in every app,
 * written by different hands on different days: a mutation fires, the server
 * refuses it, and NOTHING IS RENDERED. The button re-enables and the screen is
 * unchanged. The user cannot distinguish "the server rejected this" from "my
 * click did not register", so they click again — or, far worse, they walk away
 * believing it worked.
 *
 * That last case is the reason this is a governance problem and not a polish
 * problem. The worst instances were a rejected four-eyes approval (the approver
 * believes they approved something they did not), a rejected issuance on the
 * money path, and a failed KVKK consent withdrawal (the customer believes they
 * revoked a permission that is still live). In each, silence does not merely
 * hide an error — it asserts a false success about a governed act.
 *
 * WHY A COMPONENT AND NOT A LINT RULE. "Every mutation must render its error" is
 * not statically checkable: the error may legitimately be rendered by a parent,
 * a shared bar, or folded into an existing state. What IS achievable is making
 * the correct thing shorter to write than the incorrect thing. Before this, the
 * correct thing was a five-line `instanceof` branch repeated per call site; now
 * it is one element that accepts `mutation.error` directly and renders nothing
 * when there is no error.
 *
 * WHAT IT SHOWS. The problem document's `detail` when the API sent one — the API
 * writes those in Turkish, for operators, and they are more useful than any
 * generic string a surface could substitute. `status: 0` means fetch itself
 * rejected (offline / DNS), which is not a server verdict at all, so it gets the
 * caller's offline copy instead. Anything else — a thrown TypeError, a bug in
 * our own client — falls back to the caller's generic copy rather than leaking a
 * stack message into the UI.
 *
 * The traceId is rendered when present. An operator reading a number back over
 * the phone is the difference between a support ticket that can be found in the
 * logs and one that cannot.
 */
export function MutationError({
  error,
  fallback,
  offline,
}: {
  /** `mutation.error` straight from react-query. `null`/`undefined` renders nothing. */
  error: unknown;
  /** Generic copy for a failure that carries no problem detail. */
  fallback: string;
  /** Copy for a failed fetch (offline / unreachable). Defaults to `fallback`. */
  offline?: string;
}) {
  if (!error) return null;

  if (error instanceof ProblemError) {
    // status 0 is networkProblemError's deliberate non-HTTP sentinel.
    const text = error.status === 0 ? (offline ?? fallback) : error.problem.detail || fallback;
    return (
      <ErrorState>
        <span data-testid="mutation-error-text">{text}</span>
        {error.traceId ? (
          <span className="ui-feedback__trace" data-testid="mutation-error-trace">
            {" "}
            ({error.traceId})
          </span>
        ) : null}
      </ErrorState>
    );
  }

  return (
    <ErrorState>
      <span data-testid="mutation-error-text">{fallback}</span>
    </ErrorState>
  );
}

/**
 * Pick the error of whichever of several mutations most recently ran.
 *
 * WHY THIS EXISTS. Several surfaces render one MutationError for a pair of
 * related writes — grant/withdraw, approve/reject — and the obvious spelling is
 * `a.error ?? b.error`. That is subtly wrong: react-query KEEPS a mutation's
 * error until that same mutation next fires. So if `grant` fails and the user
 * then tries `withdraw`, which also fails, `??` still shows the stale GRANT
 * message. Both did fail, so the user is not told a falsehood about success —
 * but they are told the wrong operation failed, and on the KVKK panel that is
 * the difference between "we could not turn SMS on" and "we could not turn it
 * off", which is the only part the customer cares about.
 *
 * `submittedAt` is the mutation's own start timestamp, so the newest attempt
 * wins regardless of declaration order.
 */
export function latestError(...mutations: Array<{ error: unknown; submittedAt: number }>): unknown {
  let best: { error: unknown; submittedAt: number } | null = null;
  for (const m of mutations) {
    if (!m.error) continue;
    if (!best || m.submittedAt > best.submittedAt) best = m;
  }
  return best?.error ?? null;
}
